import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDashboardSleeveDetail, getDashboardStatus, getPaymentNotes, userEnvelopeDownloadData, documentUploadDrive } from '../../services/CommonService';
import { getRecipientList } from '../../services/Recipients';
import './Dashboard.css';
import Utils from '../../utils';
import { toast } from 'react-toastify';
import DownloadDataDoc from './Envelope/Manage/ViewEnvelope/DownloadDataDoc';
import Pagination from 'react-js-pagination';
import '../../assets/ManageSleeve.css';
import { decryptData } from '../../utils/crypto';
import { SALT } from '../../configs/AppConfig';

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, placeholder, variant }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);

  const handleSelect = (optionValue) => {
    setSelectedValue(optionValue);
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  const getDisplayText = () => {
    const option = options.find(opt => opt.value === selectedValue);
    return option ? option.label : placeholder;
  };

  const getBadgeClass = (optionValue) => {
    if (variant === 'payment') {
      const map = {
        unpaid: 'payment-status-badge payment-status-unpaid',
        partial: 'payment-status-badge payment-status-partial',
        paid: 'payment-status-badge payment-status-paid'
      };
      return map[optionValue] || 'payment-status-badge payment-status-default';
    }
    if (variant === 'case') {
      const map = {
        not_started: 'case-status-badge case-status-notstarted',
        in_progress: 'case-status-badge case-status-inprogress',
        submitted: 'case-status-badge case-status-submitted',
        done: 'case-status-badge case-status-done'
      };
      return map[optionValue] || 'case-status-badge case-status-default';
    }
    return '';
  };

  const getOptionClass = (optionValue) => {
    if (variant === 'payment') {
      const map = {
        unpaid: 'payment-unpaid',
        partial: 'payment-partial',
        paid: 'payment-paid'
      };
      return `dropdown-option ${map[optionValue] || ''}`;
    }
    if (variant === 'case') {
      const map = {
        not_started: 'case-not-started',
        in_progress: 'case-in-progress',
        submitted: 'case-submitted',
        done: 'case-done'
      };
      return `dropdown-option ${map[optionValue] || ''}`;
    }
    return 'dropdown-option';
  };

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <div className="recipient-custom-dropdown">
      <div
        className={`dropdown-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{getDisplayText()}</span>
        <i className="bi bi-chevron-down"></i>
      </div>

      {isOpen && (
        <div className="dropdown-options">
          {options.map((option) => (
            <div
              key={option.value}
              className={`${getOptionClass(option.value)} ${selectedValue === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {selectedValue === option.value && (
                <i className="bi bi-check"></i>
              )}
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="dropdown-overlay"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

const ClientManage = () => {
  const { id: encryptedId } = useParams();
  const clientId = React.useMemo(() => {
    if (!encryptedId) return null;
    try {
      const decoded = decodeURIComponent(encryptedId);
      const decrypted = decryptData(decoded, SALT);
      const asNumber = Number(decrypted);
      if (Number.isFinite(asNumber)) return asNumber;
      if (/^[0-9]+$/.test(encryptedId)) return Number(encryptedId);
      return null;
    } catch (e) {
      if (/^[0-9]+$/.test(encryptedId)) return Number(encryptedId);
      return null;
    }
  }, [encryptedId]);
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState(null);
  const [sleeveDetails, setSleeveDetails] = useState({
    status0: null,
    status1: null,
    status2: null
  });
  const [selectedStatus, setSelectedStatus] = useState('inprogress');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [caseStatus, setCaseStatus] = useState('not_started');
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadDataList, setDownloadDataList] = useState({});
  const [showCloudModal, setShowCloudModal] = useState(false);
  const [selectedEnvelopeForCloud, setSelectedEnvelopeForCloud] = useState(null);
  // Unified loading state
  const [loading, setloading] = useState(false);
  // Delete note confirmation state
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Pagination (match CompletedManageEnvelope pattern)
  const lengths = Utils.tableShowLengths();
  const [pageInprogress, setPageInprogress] = useState(1);
  const [pageDraft, setPageDraft] = useState(1);
  const [pageCompleted, setPageCompleted] = useState(1);
  const [inprogressLimit, setInprogressLimit] = useState(lengths[0]);
  const [draftLimit, setDraftLimit] = useState(lengths[0]);
  const [completedLimit, setCompletedLimit] = useState(lengths[0]);

  const handlePageChange = (statusType, newPage, totalItems, limit) => {
    const maxPage = Math.max(1, Math.ceil(totalItems / limit));
    const boundedPage = Math.min(Math.max(newPage, 1), maxPage);
    if (statusType === 'inprogress') setPageInprogress(boundedPage);
    else if (statusType === 'draft') setPageDraft(boundedPage);
    else if (statusType === 'completed') setPageCompleted(boundedPage);
  };

  const handleTableLimitChange = (statusType, event) => {
    const value = parseInt(event.target.value);
    if (statusType === 'inprogress') {
      setInprogressLimit(value);
      setPageInprogress(1);
    } else if (statusType === 'draft') {
      setDraftLimit(value);
      setPageDraft(1);
    } else if (statusType === 'completed') {
      setCompletedLimit(value);
      setPageCompleted(1);
    }
  };

  useEffect(() => {
    if (selectedStatus === 'inprogress') setPageInprogress(1);
    if (selectedStatus === 'draft') setPageDraft(1);
    if (selectedStatus === 'completed') setPageCompleted(1);
  }, [selectedStatus]);

  const DownloadDataModelRef = useRef(null);

  useEffect(() => {
    if (clientId) {
      fetchClientDetails(clientId);
      fetchSleeveDetailsForClient(clientId);
    }
  }, [clientId]);

  // Removed auto-calling getDashboardStatus on mount; it should be called only when status changes

  const fetchClientDetails = async (id) => {
    try {
      setloading(true);
      const response = await getRecipientList({
        limit: 100,
        page: 1,
        search: ''
      });

      if (response.data && response.data.data) {
        const client = response.data.data.find(recipient => recipient.id == id);
        if (client) {
          setSelectedClient(client);
          // Initialize dropdowns from API if available
          const paymentFromApi = (client.payment_status || '').toString().toLowerCase();
          if (paymentFromApi === 'unpaid' || paymentFromApi === 'partial' || paymentFromApi === 'paid') {
            setPaymentStatus(paymentFromApi);
          }

          const caseFromApi = (client.case_status || '').toString().toLowerCase();
          if (caseFromApi) {
            const caseMap = {
              'not started': 'not_started',
              'in progress': 'in_progress',
              'submitted': 'submitted',
              'done': 'done'
            };
            const normalizedCase = caseMap[caseFromApi] || caseFromApi.replace(/\s+/g, '_');
            if (['not_started', 'in_progress', 'submitted', 'done'].includes(normalizedCase)) {
              setCaseStatus(normalizedCase);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    } finally {
      setloading(false);
    }
  };

  const fetchSleeveDetailsForClient = async (recipientId) => {
    setloading(true);
    try {
      const [status0Response, status1Response, status2Response] = await Promise.all([
        getDashboardSleeveDetail({ status_id: 0, recipient_id: recipientId }),
        getDashboardSleeveDetail({ status_id: 1, recipient_id: recipientId }),
        getDashboardSleeveDetail({ status_id: 2, recipient_id: recipientId })
      ]);

      setSleeveDetails({
        status0: status0Response.data,
        status1: status1Response.data,
        status2: status2Response.data
      });
    } catch (error) {
      console.error('Error fetching sleeve details:', error);
      setSleeveDetails({
        status0: null,
        status1: null,
        status2: null
      });
    } finally {
      setloading(false);
    }
  };

  const handleStatusBadgeClick = (status) => {
    setSelectedStatus(status);
  };

  const handleManageButtonClick = (item) => {
    if (item && item.uuid) {
      navigate(`/manage/${item.uuid}`);
    } else if (item && item.id) {
      navigate(`/manage/${item.id}`);
    }
  };

  // Download and Cloud Sync handlers (parity with CompletedManageEnvelope)
  const downloadAllFileForm = (item) => {
    if (!item?.id) return;
    setloading(true);
    const obj = { envelope_id: parseInt(item.id) };
    userEnvelopeDownloadData(obj)
      .then(res => {
        setDownloadDataList(res.data.data);
        // open offcanvas
        DownloadDataModelRef?.current?.click();
        setloading(false);
      })
      .catch(err => {
        toast.error(Utils.getErrorMessage(err));
        setloading(false);
      });
  };

  const sendToCloud = (item) => {
    if (!item?.id) return;
    setloading(true);
    const obj = { id: item.id };
    documentUploadDrive(obj)
      .then(response => {
        setloading(false);
        toast.success(response.data.message);
        setShowCloudModal(false);
      })
      .catch(err => {
        toast.error(Utils.getErrorMessage(err));
        setloading(false);
        setShowCloudModal(false);
      });
  };

  const handleSendCloud = (item) => {
    if (item?.is_cloud_synced) {
      setSelectedEnvelopeForCloud(item);
      setShowCloudModal(true);
    } else {
      sendToCloud(item);
    }
  };

  const onEditEnvelope = (item) => {
    const route = item.is_smart_sleeve
      ? '/client-portal/sleeve/edit-smart/' + item.uuid + '/' + item.current_step
      : '/client-portal/sleeve/edit/' + item.uuid + '/' + item.current_step;
    navigate(route);
  };

  const handleBackClick = () => {
    navigate('/client-portal/dashboard');
  };

  const handlePaymentStatusChange = (event) => {
    const newPaymentStatus = event.target.value;
    setPaymentStatus(newPaymentStatus);
    if (!clientId) return;

    const paymentStatusReverseMap = {
      unpaid: 0,
      partial: 1,
      paid: 2
    };

    const caseStatusReverseMap = {
      not_started: 0,
      in_progress: 1,
      submitted: 2,
      done: 3
    };

    setloading(true);
    getDashboardStatus({
      recipient_id: Number(clientId),
      payment_status: paymentStatusReverseMap[newPaymentStatus],
      case_status: caseStatusReverseMap[caseStatus]
    }).then(res => {
      if (res.data && res.data.data) {
        const caseStatusMap = {
          0: 'not_started',
          1: 'in_progress',
          2: 'submitted',
          3: 'done'
        };
        setCaseStatus(caseStatusMap[res.data.data.case_status] || caseStatus);

        const paymentStatusMap = {
          0: 'unpaid',
          1: 'partial',
          2: 'paid'
        };
        setPaymentStatus(paymentStatusMap[res.data.data.payment_status] || newPaymentStatus);
      }
    }).catch(() => {
      // Keep the user-selected value in case of API error
    }).finally(() => {
      setloading(false);
    });
  };

  const handleCaseStatusChange = (event) => {
    const newCaseStatus = event.target.value;
    setCaseStatus(newCaseStatus);
    if (!clientId) return;

    const paymentStatusReverseMap = {
      unpaid: 0,
      partial: 1,
      paid: 2
    };

    const caseStatusReverseMap = {
      not_started: 0,
      in_progress: 1,
      submitted: 2,
      done: 3
    };

    setloading(true);
    getDashboardStatus({
      recipient_id: Number(clientId),
      payment_status: paymentStatusReverseMap[paymentStatus],
      case_status: caseStatusReverseMap[newCaseStatus]
    }).then(res => {
      if (res.data && res.data.data) {
        const caseStatusMap = {
          0: 'not_started',
          1: 'in_progress',
          2: 'submitted',
          3: 'done'
        };
        setCaseStatus(caseStatusMap[res.data.data.case_status] || newCaseStatus);

        const paymentStatusMap = {
          0: 'unpaid',
          1: 'partial',
          2: 'paid'
        };
        setPaymentStatus(paymentStatusMap[res.data.data.payment_status] || paymentStatus);
      }
    }).catch(() => {
      // Keep the user-selected value in case of API error
    }).finally(() => {
      setloading(false);
    });
  };

  const fetchNotes = async () => {
    if (!clientId) return;
    setloading(true);
    try {
      const res = await getPaymentNotes({ action: 2, recipient_id: Number(clientId) });
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setNotes(res.data.data);
      } else if (Array.isArray(res?.data)) {
        setNotes(res.data);
      } else {
        setNotes([]);
      }
    } catch (e) {
      setNotes([]);
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [clientId]);

  const onAddOrUpdateNote = async () => {
    if (!clientId || !noteInput.trim()) return;
    try {
      setloading(true);
      if (editingNoteId) {
        await getPaymentNotes({
          action: 3,
          recipient_id: Number(clientId),
          note_id: editingNoteId,
          note: noteInput.trim()
        });
      } else {
        await getPaymentNotes({
          action: 1,
          recipient_id: Number(clientId),
          note: noteInput.trim()
        });
      }
      setNoteInput('');
      setEditingNoteId(null);
      setIsModalOpen(false);
      fetchNotes();
    } catch (e) {
      // no-op
    } finally {
      setloading(false);
    }
  };

  const onEditNote = (note) => {
    setEditingNoteId(note.id || note.note_id);
    setNoteInput(note.note || '');
    setIsModalOpen(true);
  };

  const onCancelEdit = () => {
    setEditingNoteId(null);
    setNoteInput('');
    setIsModalOpen(false);
  };

  const onDeleteNote = async (note) => {
    if (!clientId) return;
    try {
      setloading(true);
      await getPaymentNotes({
        action: 4,
        recipient_id: Number(clientId),
        note_id: note.id || note.note_id
      });
      if (editingNoteId && (note.id === editingNoteId || note.note_id === editingNoteId)) {
        setEditingNoteId(null);
        setNoteInput('');
      }
      fetchNotes();
    } catch (e) {
      // no-op
    } finally {
      setloading(false);
      setShowDeleteNoteModal(false);
      setNoteToDelete(null);
    }
  };

  const openDeleteNoteModal = (note) => {
    setNoteToDelete(note);
    setShowDeleteNoteModal(true);
  };

  const cancelDeleteNote = () => {
    setShowDeleteNoteModal(false);
    setNoteToDelete(null);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;
    await onDeleteNote(noteToDelete);
  };

  const renderDataTable = (responseData, statusType) => {
    if (!responseData) {
      // Render table structure even when there is no data
      if (statusType === 'inprogress') {
        return (
          <div className="table-responsive">
            <table className="table table-striped table-hover table-sm table-elevated">
              <thead className="table-dark-header">
                <tr>
                  <th className="col-sno th-compact">#</th>
                  <th className="th-compact">Sleeve Name</th>
                  <th className="th-compact">Due Date</th>
                  <th className="text-center th-compact">Awaiting</th>
                  <th className="text-center th-compact">Needs Review</th>
                  <th className="text-center th-compact">Approved</th>
                  <th className="text-center th-compact">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
      if (statusType === 'draft') {
        return (
          <div className="table-responsive">
            <table className="table table-striped table-hover table-sm table-elevated">
              <thead className="table-dark-header">
                <tr>
                  <th className="th-compact">Created</th>
                  <th className="th-compact">Sleeve Name</th>
                  <th className="th-compact">Due Date</th>
                  <th className="th-compact">User</th>
                  <th className="text-center th-compact">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
      if (statusType === 'completed') {
        return (
          <div className="table-responsive">
            <table className="table table-striped table-hover table-sm table-elevated">
              <thead className="table-dark-header">
                <tr>
                  <th className="th-compact">Created</th>
                  <th className="th-compact">Sleeve Name</th>
                  <th className="th-compact">Due Date</th>
                  <th className="th-compact">User</th>
                  <th className="text-center th-compact">Download</th>
                  <th className="text-center th-compact">Cloud Sync</th>
                  <th className="text-center th-compact">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
    }

    if (responseData.data && Array.isArray(responseData.data)) {
      const dataArray = responseData.data;

      // Pagination calculations (shared for all three tables) - using dynamic limits
      const totalItems = dataArray.length;
      const currentPage =
        statusType === 'inprogress' ? pageInprogress : statusType === 'draft' ? pageDraft : pageCompleted;
      const currentLimit =
        statusType === 'inprogress' ? inprogressLimit : statusType === 'draft' ? draftLimit : completedLimit;
      const shouldPaginate = totalItems > currentLimit;
      const startIndex = shouldPaginate ? (currentPage - 1) * currentLimit : 0;
      const endIndex = shouldPaginate ? Math.min(startIndex + currentLimit, totalItems) : totalItems;
      const pageData = dataArray.slice(startIndex, endIndex);
      const maxPage = Math.max(1, Math.ceil(totalItems / currentLimit));

      if (statusType === 'inprogress') {
        return (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-hover table-elevated">
                <thead className="table-dark-header">
                  <tr>
                    <th className="col-sno th-compact">#</th>
                    <th className="th-compact">Sleeve Name</th>
                    <th className="th-compact">Due Date</th>
                    <th className="text-center th-compact">Awaiting</th>
                    <th className="text-center th-compact">Needs Review</th>
                    <th className="text-center th-compact">Approved</th>
                    <th className="text-center th-compact">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">No data available</td>
                    </tr>
                  ) : pageData.map((item, index) => {
                    const r = Array.isArray(item.recipient_list) && item.recipient_list.length > 0 ? item.recipient_list[0] : null;
                    const awaiting = r?.awaiting ?? 0;
                    const needReview = r?.need_review ?? 0;
                    const approved = r?.approved ?? 0;

                    return (
                      <tr key={index} className={item.is_smart_sleeve ? 'smart-sleeve-row' : ''}>
                        <td className="fw-bold text-secondary col-sno">
                          {startIndex + index + 1}
                        </td>
                        <td className="cell-strong">
                          <div className="d-flex align-items-center">
                            <span>{item.envelope_name || 'N/A'}</span>
                            {item.is_smart_sleeve && (
                              <span className="smart-sleeve-badge ms-2" title="Smart Sleeve">
                                <i className="bi bi-lightning-fill"></i>Smart
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-muted">
                          {item.due_date || 'N/A'}
                        </td>
                        <td className="text-center">
                          <span className="status-badge-inrpogress status-awaiting">
                            {awaiting}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="status-badge-inrpogress status-need-review">
                            {needReview}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="status-badge-inrpogress status-approved">
                            {approved}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleManageButtonClick(item)}
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalItems > currentLimit && (
              <div className="table_footer_wrap">
                <div className="d-flex align-items-center mb-2">
                  <span className="me-2 text-dark">Show</span>
                  <select
                    className="form-select"
                    value={currentLimit}
                    style={{ width: '80px' }}
                    onChange={(e) => handleTableLimitChange(statusType, e)}
                    aria-label="Rows per page"
                  >
                    {lengths && lengths.map((len, i) => (
                      <option value={len} key={i}>{len}</option>
                    ))}
                  </select>
                </div>
                <div className="pagination mb-2">
                  <Pagination
                    activePage={currentPage}
                    itemsCountPerPage={currentLimit}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={5}
                    onChange={(e) => handlePageChange(statusType, e, totalItems, currentLimit)}
                  />
                </div>
              </div>
            )}
          </>
        );
      }

      if (statusType === 'draft') {
        return (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm table-elevated">
                <thead className="table-dark-header">
                  <tr>
                    <th className="th-compact">Created</th>
                    <th className="th-compact">Sleeve Name</th>
                    <th className="th-compact">Due Date</th>
                    <th className="th-compact">User</th>
                    <th className="text-center th-compact">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">No data available</td>
                    </tr>
                  ) : pageData.map((item, index) => (
                    <tr key={index} className={item.is_smart_sleeve ? 'smart-sleeve-row' : ''}>
                      <td className="text-muted">
                        {item.created_at || 'N/A'}
                      </td>
                      <td className="cell-strong">
                        <div className="d-flex align-items-center">
                          <span>{item.envelope_name || 'N/A'}</span>
                          {item.is_smart_sleeve && (
                            <span className="smart-sleeve-badge ms-2" title="Smart Sleeve">
                              <i className="bi bi-lightning-fill"></i>Smart
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-muted">
                        {item.due_date || 'N/A'}
                      </td>
                      <td className="text-muted">
                        {item.sender_name || item.sender_id || 'N/A'}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => onEditEnvelope(item)}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalItems > currentLimit && (
              <div className="table_footer_wrap">
                <div className="d-flex align-items-center mb-2">
                  <span className="me-2 text-dark">Show</span>
                  <select
                    className="form-select"
                    value={currentLimit}
                    style={{ width: '80px' }}
                    onChange={(e) => handleTableLimitChange(statusType, e)}
                    aria-label="Rows per page"
                  >
                    {lengths && lengths.map((len, i) => (
                      <option value={len} key={i}>{len}</option>
                    ))}
                  </select>
                </div>
                <div className="pagination mb-2">
                  <Pagination
                    activePage={currentPage}
                    itemsCountPerPage={currentLimit}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={5}
                    onChange={(e) => handlePageChange(statusType, e, totalItems, currentLimit)}
                  />
                </div>
              </div>
            )}
          </>
        );
      }

      if (statusType === 'completed') {
        return (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-hover border">
                <thead className="table-dark-header">
                  <tr>
                    <th className="th-compact">Created</th>
                    <th className="th-compact">Sleeve Name</th>
                    <th className="th-compact">Due Date</th>
                    <th className="th-compact">User</th>
                    <th className="text-center th-compact">Download</th>
                    <th className="text-center th-compact">Cloud Sync</th>
                    <th className="text-center th-compact">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">No data available</td>
                    </tr>
                  ) : pageData.map((item, index) => (
                    <tr key={index} className={item.is_smart_sleeve ? 'smart-sleeve-row' : ''}>
                      <td className="text-muted">
                        {item.created_at || 'N/A'}
                      </td>
                      <td className="cell-strong">
                        <div className="d-flex align-items-center">
                          <span>{item.envelope_name || 'N/A'}</span>
                          {item.is_smart_sleeve && (
                            <span className="smart-sleeve-badge ms-2" title="Smart Sleeve">
                              <i className="bi bi-lightning-fill"></i>Smart
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-muted">
                        {item.due_date || 'N/A'}
                      </td>
                      <td className="text-muted">
                        {item.sender_name || item.sender_id || 'N/A'}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-link p-0"
                          onClick={() => downloadAllFileForm(item)}
                          title="Click to Download"
                        >
                          <i className={`bi bi-cloud-arrow-down-fill fs-4 ${item.is_downloaded ? 'text-success' : 'text-muted'}`} />
                        </button>
                      </td>
                      <td className="text-center">
                        {(item.cloud_sync && item.cloud_type &&
                          item.cloud_sync.split(',').map(Number).some(v => v > 0) &&
                          item.cloud_type.split(',').map(Number).some(v => v > 0)) && (
                            <button
                              className="btn btn-link p-0"
                              onClick={() => handleSendCloud(item)}
                              title="Send to Cloud"
                            >
                              <i className="bi bi-cloud-arrow-up fs-4 text-primary" />
                            </button>
                          )}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleManageButtonClick(item)}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalItems > currentLimit && (
              <div className="table_footer_wrap">
                <div className="d-flex align-items-center mb-2">
                  <span className="me-2 text-dark">Show</span>
                  <select
                    className="form-select"
                    value={currentLimit}
                    style={{ width: '80px' }}
                    onChange={(e) => handleTableLimitChange(statusType, e)}
                    aria-label="Rows per page"
                  >
                    {lengths && lengths.map((len, i) => (
                      <option value={len} key={i}>{len}</option>
                    ))}
                  </select>
                </div>
                <div className="pagination mb-2">
                  <Pagination
                    activePage={currentPage}
                    itemsCountPerPage={currentLimit}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={5}
                    onChange={(e) => handlePageChange(statusType, e, totalItems, currentLimit)}
                  />
                </div>
              </div>
            )}
          </>
        );
      }

      const allKeys = [...new Set(dataArray.flatMap(item => Object.keys(item)))];

      return (
        <div className="table-responsive">
          <table className="table table-striped table-hover border">
            <thead className="table-dark">
              <tr>
                <th className="col-sno">#</th>
                {allKeys.map(key => (
                  <th key={key} className="text-nowrap">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">No data available</td>
                </tr>
              ) : dataArray.map((item, index) => (
                <tr key={index}>
                  <td className="text-muted fw-bold">{index + 1}</td>
                  {allKeys.map(key => (
                    <td key={key} className="text-break">
                      {item[key] !== null && item[key] !== undefined ?
                        (Array.isArray(item[key]) ?
                          <span className="badge bg-secondary">{item[key].join(', ')}</span> :
                          (typeof item[key] === 'object' ?
                            <code className="text-dark">{JSON.stringify(item[key])}</code> :
                            (key.includes('date') || key.includes('_at') ?
                              <small className="text-muted">{String(item[key])}</small> :
                              String(item[key])
                            )
                          )
                        ) :
                        <span className="text-muted">N/A</span>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const entries = Object.entries(responseData);

    return (
      <div className="table-responsive">
        <table className="table table-striped table-hover border">
          <thead className="table-dark">
            <tr>
              <th className="col-30">Property</th>
              <th className="col-70">Value</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">No data available</td>
              </tr>
            ) : entries.map(([key, value]) => (
              <tr key={key}>
                <td className="fw-bold text-nowrap">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </td>
                <td className="text-break">
                  {value !== null && value !== undefined ?
                    (Array.isArray(value) ?
                      <span className="badge bg-info">{value.join(', ')}</span> :
                      (typeof value === 'object' ?
                        <code className="text-dark">{JSON.stringify(value, null, 2)}</code> :
                        String(value)
                      )
                    ) :
                    <span className="text-muted">N/A</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}

      <div className='section-bg-color dashboard-container mt-5'>
        <div className='custom_container'>
          <div className='dashboard-content'>
            {/* Header Section */}
            <div className='dashboard-header my-4'>
              <div className='dashboard-header-flex'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className="recipient-details">
                    <h4 className="text-white mb-1 fw-bold fs-2">Recipient Details</h4>
                    <p className="text-white mb-0">Manage recipient information and sleeve status</p>
                  </div>
                  <div className="d-flex align-items-center">
                    <button
                      className='btn btn-light'
                      onClick={handleBackClick}
                      title="Back to Dashboard"
                    >
                      <i className="bi bi-arrow-left me-2"></i> Back
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Cards Section */}
            <div className="row mb-4">
              {/* Payment Status Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 recipient-card">
                  <div className="payment-status-card">
                    <div className="d-flex align-items-center mb-3">
                      <div className="status-icon me-3 bg-tint-blue">
                        <i className="bi bi-credit-card text-primary"></i>
                      </div>
                      <div>
                        <h6 className="payment-notes-title">Payment Status</h6>
                        <p className="text-muted mb-0 small">Update payment information</p>
                      </div>
                    </div>
                    <div className="">
                      <CustomDropdown
                        value={paymentStatus}
                        onChange={handlePaymentStatusChange}
                        variant="payment"
                        options={[
                          { value: 'unpaid', label: 'Unpaid' },
                          { value: 'partial', label: 'Partial' },
                          { value: 'paid', label: 'Paid' }
                        ]}
                        placeholder="Select"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Case Status Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 recipient-card">
                  <div className="case-status-card">
                    <div className="d-flex align-items-center mb-3">
                      <div className="status-icon me-3 bg-tint-purple">
                        <i className="bi bi-folder text-purple"></i>
                      </div>
                      <div>
                        <h6 className="payment-notes-title  ">Case Status</h6>
                        <p className="text-muted mb-0 small">Track case progress</p>
                      </div>
                    </div>
                    <div className="">
                      <CustomDropdown
                        value={caseStatus}
                        onChange={handleCaseStatusChange}
                        variant="case"
                        options={[
                          { value: 'not_started', label: 'Not Started' },
                          { value: 'in_progress', label: 'In Progress' },
                          { value: 'submitted', label: 'Submitted' },
                          { value: 'done', label: 'Done' }
                        ]}
                        placeholder="Select"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="recipient-card p-4 mb-5">
              <div className="payment-notes-header">
                <h5 className="payment-notes-title">Notes</h5>
                <button className="add-note-btn" onClick={() => setIsModalOpen(true)}>
                  <i className="bi bi-plus"></i>
                  Add Note
                </button>
              </div>

              <div className="notes-container">
                {notes.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-chat-dots empty-icon"></i>
                    <p className="mt-2">No notes available</p>
                  </div>
                ) : (
                  notes.map((note) => (
                    <div key={note.id || note.note_id} className="note-card d-flex justify-content-between align-items-center">
                      <div>
                        <div className="note-content">{note.note}</div>
                        <div className="note-meta">

                          <span className="timestamp">{note.created_at}</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 justify-content-end">
                        <button
                          className="edit-note-btn"
                          onClick={() => onEditNote(note)}
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Edit note"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="delete-note-btn"
                          onClick={() => openDeleteNoteModal(note)}
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Delete note"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add/Edit Note Modal */}
              {isModalOpen && (
                <>
                  <div className="modal-backdrop fade show" onClick={() => setIsModalOpen(false)}></div>
                  <div className="modal fade show d-block"
                    tabIndex="-1"
                    aria-labelledby="noteModalLabel"
                    aria-hidden="false">
                    <div className="modal-dialog modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title" id="noteModalLabel">
                            {editingNoteId ? 'Edit Note' : 'Add Note'}
                          </h5>
                          <button type="button"
                            className="btn-close"
                            onClick={() => setIsModalOpen(false)}
                            aria-label="Close">
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </div>

                        <div className="modal-body">
                          <div className="mb-3">
                            <label htmlFor="noteTextarea" className="form-label">Note</label>
                            <textarea
                              className="form-control note-textarea"
                              id="noteTextarea"
                              placeholder="Enter payment details, amount received, etc..."
                              value={noteInput}
                              onChange={(e) => setNoteInput(e.target.value)}
                              rows={4}
                            ></textarea>
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button type="button"
                            className="btn btn-secondary"
                            onClick={() => setIsModalOpen(false)}>
                            Cancel
                          </button>
                          <button type="button"
                            className="btn btn-primary"
                            onClick={onAddOrUpdateNote}
                          >
                            {editingNoteId ? 'Update ' : 'Save '}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Hidden trigger for DownloadDataDoc offcanvas */}
            <div ref={DownloadDataModelRef} data-bs-toggle="offcanvas" data-bs-target="#DownloadDataDoc" aria-controls="DownloadDataDoc" />
            {/* Download offcanvas component */}
            <DownloadDataDoc setLoading={setloading} downloadDataList={downloadDataList} setDownloadDataList={setDownloadDataList} />

            {/* Delete Note Confirmation Modal */}
            <div className={`modal fade ${showDeleteNoteModal ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-labelledby="deleteNoteModalLabel" aria-hidden={!showDeleteNoteModal}>
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="deleteNoteModalLabel">Confirm Delete</h5>
                    <button type="button" className="btn-close" onClick={cancelDeleteNote} aria-label="Close">
                      <i className="bi bi-x-circle"></i>
                    </button>
                  </div>
                  <div className="modal-body">
                    Are you sure you want to delete this note?
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={cancelDeleteNote}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-danger" onClick={confirmDeleteNote}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showDeleteNoteModal && <div className="modal-backdrop fade show"></div>}

            {/* Cloud Sync Confirmation Modal */}
            <div className={`modal fade ${showCloudModal ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-labelledby="cloudSyncModalLabel" aria-hidden={!showCloudModal}>
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="cloudSyncModalLabel">Confirm Cloud Sync</h5>
                    <button type="button" className="btn-close" onClick={() => setShowCloudModal(false)} aria-label="Close">
                      <i className="bi bi-x-circle"></i>
                    </button>
                  </div>
                  <div className="modal-body">
                    Are you sure you want to sync this sleeve to cloud?
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCloudModal(false)}>
                      No
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => sendToCloud(selectedEnvelopeForCloud)}>
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showCloudModal && <div className="modal-backdrop fade show"></div>}
          </div>

          {/* Main Content Card */}
          <div className="recipient-card p-4 ">
            <div className="">
              <div className="sleeve-details h-100 d-flex flex-column">
                {/* Status Tabs */}
                <div className="d-sm-block d-md-flex justify-content-between align-items-center flex-shrink-0">
                  <h6 className="payment-notes-title mb-4">Sleeve Details</h6>
                  <div className="status-badges-container mb-4">
                    <div
                      className={`custom-status-badge  inprogress ${selectedStatus === 'inprogress' ? 'active' : ''}`}
                      onClick={() => handleStatusBadgeClick('inprogress')}

                    >
                      <i className="bi bi-clock me-2"></i>
                      In Progress
                    </div>
                    <div
                      className={`custom-status-badge draft ${selectedStatus === 'draft' ? 'active' : ''}`}
                      onClick={() => handleStatusBadgeClick('draft')}

                    >
                      <i className="bi bi-file-earmark me-2"></i>
                      Draft
                    </div>
                    <div
                      className={`custom-status-badge completed ${selectedStatus === 'completed' ? 'active' : ''}`}
                      onClick={() => handleStatusBadgeClick('completed')}

                    >
                      <i className="bi bi-check-circle me-2"></i>
                      Completed
                    </div>
                  </div>
                </div>

                {/* Data Table Section */}
                <div className="flex-grow-1 overflow-hidden">
                  {loading ? (
                    <div className="text-center py-4 h-100 d-flex flex-column justify-content-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading data...</p>
                    </div>
                  ) : (
                    <div className="row h-100">
                      <div className="col-12 h-100">
                        {selectedStatus === 'draft' && (
                          <div className="h-100">
                            <div className="card-body p-0 h-100">
                              <div className="h-100 scroll-auto">
                                {renderDataTable(sleeveDetails.status0, 'draft')}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedStatus === 'inprogress' && (
                          <div className="h-100">
                            <div className="card-body p-0 h-100">
                              <div className="h-100 scroll-auto">
                                {renderDataTable(sleeveDetails.status1, 'inprogress')}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedStatus === 'completed' && (
                          <div className="h-100">
                            <div className="card-body p-0 h-100">
                              <div className="h-100 scroll-auto">
                                {renderDataTable(sleeveDetails.status2, 'completed')}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientManage; 